import { RootStateOrAny, useSelector } from 'react-redux'

import { Tooltip, Typography } from 'antd'
import styled from 'styled-components'

import { colours } from '@qflow/theme'

import { UTILITIES } from 'portal/components/Utilities/common/constants'
import { ColourButton, icons } from 'portal/lib/primitives'
import { Meter } from './Interface'

type MeterPanelProps = {
  meter: Meter
  readOnly: boolean
  onEdit: () => void
}

export const MeterPanel = ({ meter, readOnly, onEdit }: MeterPanelProps) => {
  const projectTimeZone = useSelector(
    (state: RootStateOrAny) => state.projectDetails.modules?.timeZone
  )

  const { Title } = Typography

  const DateFormatShort: Intl.DateTimeFormatOptions = {
    timeZone: projectTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }

  return (
    <MeterPanelWrapper id={meter.meterId as string}>
      <Row>
        <Col>
          <Title level={5}>{meter.meterName}</Title>
          <Reference>{meter.meterReference}</Reference>
        </Col>
        <Col>
          <ColourButton.Basic onClick={onEdit} disabled={readOnly} title="Edit">
            <Tooltip
              title={
                readOnly
                  ? 'You do not have permission to add or edit meters.'
                  : null
              }
              placement="left"
            >
              <icons.EditPencil />
            </Tooltip>
          </ColourButton.Basic>
        </Col>
      </Row>
      <Row>
        <Col>
          <div>Latest reading</div>
          {meter.latestReading && (
            <>
              <div>
                <b>
                  {meter.latestReading.actualValue}{' '}
                  {meter.utilityType === UTILITIES.WATER ? (
                    <>
                      m<sup>3</sup>
                    </>
                  ) : (
                    'kWh'
                  )}
                </b>{' '}
                <span>
                  {new Date(meter.latestReading.readingDateTime).toLocaleString(
                    [],
                    DateFormatShort
                  )}
                </span>
              </div>
              <div>
                {meter.latestReading.isEstimated ? (
                  <Estimate>Estimate</Estimate>
                ) : (
                  <Actual>Actual</Actual>
                )}
              </div>
            </>
          )}
          {!meter.latestReading && (
            <div>
              <b>No readings added</b>
            </div>
          )}
        </Col>
      </Row>
    </MeterPanelWrapper>
  )
}

const MeterPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 20%;
  justify-content: space-between;
  padding: 1rem 0.75rem 0.5rem 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;
  gap: 0.5rem;
  min-width: 272px;
  max-width: 24%;

  h5 {
    margin-bottom: 0.125rem;
  }

  button {
    opacity: 0;
    border: none;
  }

  &:hover {
    background-color: ${colours.LIGHT_GREY};

    button {
      opacity: 1;
    }
  }
`
const Row = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    margin-bottom: 0.5rem;
  }
`
const Col = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const Reference = styled.div`
  word-break: break-word;
`
const Estimate = styled.span`
  display: inline-block;
  padding: 0.125rem 0.625rem;
  margin-top: 0.5rem;
  border-radius: 3px;

  color: ${colours.ORANGE};
  border: 1px solid ${colours.ORANGE};
  background-color: ${colours.ORANGE}11;
`
const Actual = styled(Estimate)`
  color: ${colours.GREEN};
  border: 1px solid ${colours.GREEN};
  background-color: ${colours.GREEN}11;
`
